import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import {
  useFeatureFlag,
  useGetAllFeatureFlags,
} from "@/contexts/FeatureFlagContext"
import { useDataGapsStats } from "@/features/material-compliance/api/dataGapsApi"
import type { DataGapLine } from "@/features/material-compliance/api/types/dataGaps"
import {
  firstEnabledTabPath,
  MC_BASE,
  STUDIO_ICONS_V2,
  STUDIOS,
  type StudioTab,
} from "@/features/material-compliance/studios"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { Check, ChevronRight, ShieldCheck } from "lucide-react"
import { Fragment } from "react"
import {
  Link,
  matchPath,
  NavLink,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router"

interface BreadcrumbCrumb {
  labelKey: TranslationKeys
  // Omitted on the last crumb — it's always plain text (current page).
  to?: string
}

interface BreadcrumbRoute {
  pattern: string
  crumbs: BreadcrumbCrumb[]
}

const REQUESTS_CRUMB: BreadcrumbCrumb = {
  labelKey: "MC_CS_TAB_REQUESTS",
  to: `${MC_BASE}/customer-studio/requests`,
}
const DECLARATIONS_CRUMB: BreadcrumbCrumb = {
  labelKey: "MC_SD_TAB_DECLARATIONS",
  to: `${MC_BASE}/supplier-studio/declarations`,
}
const INQUIRY_CRUMB: BreadcrumbCrumb = {
  labelKey: "MC_SD_TAB_INQUIRY",
  to: `${MC_BASE}/supplier-studio/inquiry`,
}

const BREADCRUMB_ROUTES: BreadcrumbRoute[] = [
  {
    pattern: `${MC_BASE}/customer-studio/requests/import/confirm`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_IMPORT" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/import`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_IMPORT" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/:id/process/mapping`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_MAPPING" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/:id/process/reconcile`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_RECONCILE" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/:id/process/gaps`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_GAPS" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/:id/process/review`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_REVIEW" }],
  },
  {
    // Bare .../process — transient, redirects to the canonical step.
    pattern: `${MC_BASE}/customer-studio/requests/:id/process`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_DETAILS" }],
  },
  {
    pattern: `${MC_BASE}/customer-studio/requests/:id`,
    crumbs: [REQUESTS_CRUMB, { labelKey: "MC_BC_DETAILS" }],
  },
  {
    pattern: `${MC_BASE}/supplier-studio/declarations/:id/review`,
    crumbs: [DECLARATIONS_CRUMB, { labelKey: "MC_BC_REVIEW" }],
  },
  {
    pattern: `${MC_BASE}/supplier-studio/inquiry/draft/success`,
    crumbs: [INQUIRY_CRUMB, { labelKey: "MC_BC_DRAFT" }],
  },
  {
    pattern: `${MC_BASE}/supplier-studio/inquiry/draft`,
    crumbs: [INQUIRY_CRUMB, { labelKey: "MC_BC_DRAFT" }],
  },
  {
    pattern: `${MC_BASE}/supplier-studio/inquiry/new`,
    crumbs: [INQUIRY_CRUMB, { labelKey: "MC_BC_NEW" }],
  },
  {
    // Flat wizard routes — no sub-module tab to link back to.
    pattern: `${MC_BASE}/master-data/import`,
    crumbs: [{ labelKey: "MC_BC_IMPORT" }],
  },
  {
    pattern: `${MC_BASE}/master-data/processing`,
    crumbs: [{ labelKey: "MC_BC_PROCESSING" }],
  },
  {
    pattern: `${MC_BASE}/master-data/mapping`,
    crumbs: [{ labelKey: "MC_BC_MAPPING" }],
  },
]

// TUR-1539: matches a gap line's listUrl against a tab's `to` path, ignoring
// the query string, so `?no_supplier=1` on the backend URL never breaks a
// simple prefix compare.
function gapLinePathname(listUrl: string): string {
  const queryIndex = listUrl.indexOf("?")
  return queryIndex === -1 ? listUrl : listUrl.slice(0, queryIndex)
}

/** Tooltip sentence per gap line id (backend `mc_data_gaps.DataGapLine.id`). */
const GAP_PILL_TIP_KEYS: Partial<Record<string, TranslationKeys>> = {
  suppliers_awaiting: "MC_MD_GAPS_PILL_TIP_SUPPLIERS_AWAITING",
  suppliers_never_asked: "MC_MD_GAPS_PILL_TIP_SUPPLIERS_NEVER_ASKED",
  articles_no_supplier: "MC_MD_GAPS_PILL_TIP_ARTICLES_NO_SUPPLIER",
  products_no_articles: "MC_MD_GAPS_PILL_TIP_PRODUCTS_NO_ARTICLES",
}

/**
 * One tab of the studio strip. The gap pill is a control of its own, so it
 * sits NEXT to the link, not inside it: a button nested in an anchor is
 * invalid HTML and reads as one tangled control to assistive tech. The
 * wrapper carries the tab's border and active colour so both keep the look
 * they had as one element; the link is only the icon and label.
 */
function StudioTabLink({
  tab,
  gapLine,
}: {
  tab: StudioTab
  gapLine: DataGapLine | null
}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  // Same rule NavLink applies for its own isActive, read here for the wrapper.
  const active = useMatch({ path: tab.to, end: tab.end ?? false }) !== null
  const Icon = tab.icon
  // A gap the strip knows gets its own plain sentence ("17 suppliers have not
  // answered your inquiry yet"); any other line keeps the generic template.
  const pillTipKey = gapLine ? GAP_PILL_TIP_KEYS[gapLine.id] : undefined
  const pillLabel = gapLine
    ? pillTipKey
      ? translate(pillTipKey, { n: gapLine.count })
      : translate("MC_MD_GAPS_PILL_TIP", {
          label: translate(gapLine.labelKey),
          n: gapLine.count,
          unit: translate(gapLine.unitKey),
        })
    : null

  return (
    <div
      className={cn(
        "inline-flex h-8 shrink-0 items-center rounded-md border text-sm font-semibold transition-colors",
        active
          ? "border-yellow1 text-yellow1"
          : "border-transparent text-grey5 hover:text-white",
      )}
    >
      <NavLink
        to={tab.to}
        end={tab.end}
        className={cn(
          "inline-flex h-full items-center gap-2 pl-3.5",
          gapLine ? "pr-0" : "pr-3.5",
        )}
      >
        <Icon size={15} />
        <span className="whitespace-nowrap">{translate(tab.labelKey)}</span>
      </NavLink>
      {gapLine && pillLabel && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  if (gapLine.listUrl) navigate(gapLine.listUrl)
                }}
                aria-label={pillLabel}
                className="ml-1.5 mr-3.5 inline-flex h-4 min-w-4 cursor-pointer items-center justify-center rounded-full bg-redLight/15 px-1 text-[10px] font-semibold tabular-nums text-redLight outline-hidden transition-colors hover:bg-redLight/25 focus-visible:ring-1 focus-visible:ring-ring"
              >
                {gapLine.count}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="max-w-[260px] border-grey4 bg-grey1 text-xs text-white"
            >
              {pillLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}

export function MaterialComplianceNav() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const flags = useGetAllFeatureFlags()
  const sidebarV2Enabled = useFeatureFlag("SIDEBAR_V2")
  const { hasPermission } = useUser()

  // Only surface studios/tabs whose flags are enabled — mirrors the gating
  // in material-compliance/App.tsx so the nav never links to a route that
  // resolves to the feature-unavailable page.
  const visibleStudios = STUDIOS.filter(
    (s) =>
      flags[s.flag] && (s.id !== "company-index" || flags.MC_SUPPLIER_STUDIO),
  ).map((s) => {
    const tabs = s.tabs.filter((t) => flags[t.flag])
    // Land on the first enabled tab rather than a hardcoded one that may be disabled.
    // SIDEBAR_V2: resolve the icon through the shared V2 catalog (master-data
    // swaps Database for Layers); legacy keeps each studio's own icon.
    const icon = sidebarV2Enabled ? (STUDIO_ICONS_V2[s.id] ?? s.icon) : s.icon
    return { ...s, tabs, to: firstEnabledTabPath(s, flags), icon }
  })

  const isHome =
    flags.MC_HOME && (pathname === MC_BASE || pathname === `${MC_BASE}/`)

  const homeEntry: (typeof visibleStudios)[number] = {
    id: "home",
    labelKey: "NAV_MATERIAL_COMPLIANCE",
    icon: ShieldCheck,
    to: MC_BASE,
    flag: "MC_HOME",
    tabs: [],
  }

  // SIDEBAR_V2 dedups the header: the group's own sidebar entry already
  // covers the module home, so the dropdown drops its redundant home row.
  const menuEntries =
    !sidebarV2Enabled && flags.MC_HOME
      ? [homeEntry, ...visibleStudios]
      : visibleStudios

  const segments = new Set(pathname.split("/"))
  const activeStudio = isHome
    ? homeEntry
    : (visibleStudios.find((s) => segments.has(s.id)) ??
      visibleStudios[0] ??
      (flags.MC_HOME ? homeEntry : undefined))

  // Only master-data's own tab strip reads the gap counts, and the endpoint
  // sits behind MC_MASTER_DATA_VIEW, so the request only goes out there and
  // for members who may see it (the studio is only active when its flag is
  // on: visibleStudios filters on it). Called unconditionally, rules of hooks.
  const gaps = useDataGapsStats({
    enabled:
      activeStudio?.id === "master-data" &&
      hasPermission(PERMISSIONS.MC_MASTER_DATA_VIEW),
  })

  if (!activeStudio) return null

  const ActiveIcon = activeStudio.icon
  const breadcrumbRoute = BREADCRUMB_ROUTES.find((route) =>
    matchPath({ path: route.pattern, end: true }, pathname),
  )

  return (
    <div className={cn("flex items-center", breadcrumbRoute ? "" : "gap-3")}>
      <DropdownMenu>
        <DropdownMenuTrigger className="group inline-flex h-9 items-center gap-2.5 rounded-md px-2.5 text-xs font-medium uppercase tracking-widest text-white outline-hidden transition-colors hover:bg-grey2 focus-visible:ring-1 focus-visible:ring-ring">
          <ActiveIcon size={15} className="text-yellow1" />
          <span className="whitespace-nowrap">
            {translate(activeStudio.labelKey)}
          </span>
          <ChevronRight
            size={15}
            className="text-grey5 transition-transform group-data-[state=open]:rotate-90 group-data-[state=open]:text-yellow1"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="min-w-[220px] border-grey4 bg-grey2 p-1.5"
        >
          {menuEntries.map((studio) => {
            const Icon = studio.icon
            const isActive = studio.id === activeStudio.id
            return (
              <DropdownMenuItem
                key={studio.id}
                onSelect={() => navigate(studio.to)}
                className={cn(
                  "flex h-[38px] cursor-pointer items-center gap-2.5 rounded-md px-3 text-sm focus:bg-grey3 focus:text-white",
                  isActive ? "text-yellow1" : "text-white",
                )}
              >
                <Icon
                  size={16}
                  className={cn(
                    "shrink-0",
                    isActive ? "text-yellow1" : "text-grey5",
                  )}
                />
                <span className="flex-1">{translate(studio.labelKey)}</span>
                {isActive && <Check size={15} className="text-yellow1" />}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {breadcrumbRoute ? (
        <Breadcrumb>
          <BreadcrumbList className="flex-nowrap gap-1.5 text-sm sm:gap-1.5">
            {breadcrumbRoute.crumbs.map((crumb, index) => {
              const isLast = index === breadcrumbRoute.crumbs.length - 1
              return (
                <Fragment key={crumb.labelKey}>
                  {index > 0 && (
                    <BreadcrumbSeparator>
                      <ChevronRight size={14} className="text-grey5" />
                    </BreadcrumbSeparator>
                  )}
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="text-grey5">
                        {translate(crumb.labelKey)}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        asChild
                        className="text-grey5 hover:text-white"
                      >
                        <Link to={crumb.to as string}>
                          {translate(crumb.labelKey)}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </Fragment>
              )
            })}
          </BreadcrumbList>
        </Breadcrumb>
      ) : (
        activeStudio.tabs.length > 0 && (
          <div className="inline-flex items-center gap-1 overflow-x-auto rounded-lg border border-grey4 p-1 scrollbar-none [&::-webkit-scrollbar]:hidden">
            {activeStudio.tabs.map((tab) => {
              // TUR-1539: only the master-data tab strip surfaces a gap
              // count; the first blocking, non-zero gap line whose listUrl
              // targets this tab.
              const gapLine =
                activeStudio.id === "master-data"
                  ? (gaps.data?.lines.find(
                      (line) =>
                        line.blocking &&
                        line.count > 0 &&
                        line.listUrl &&
                        gapLinePathname(line.listUrl).startsWith(tab.to),
                    ) ?? null)
                  : null
              return <StudioTabLink key={tab.to} tab={tab} gapLine={gapLine} />
            })}
          </div>
        )
      )}
    </div>
  )
}
